import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import {
  ActionsDialogue, Bouton, Carte, Champ, Dialogue, EnTetePage, Etiquette,
  IconePlus, Message, Selecteur, SqueletteTableau, Vide, ZoneTexte, useRetours,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useAbonnement } from '../lib/droits.tsx';
import { useSession } from '../lib/session.tsx';

/**
 * Support — côté enseignant.
 *
 * ── Un fil, pas une adresse de courriel ─────────────────────────────────────
 * Une adresse disparaît dans une messagerie personnelle, entre deux circulaires
 * de circonscription. Un fil reste dans l'application, à côté de ce dont il
 * parle, et l'on y retrouve ce qu'on avait demandé six mois plus tôt.
 *
 * ── Ce que l'écran promet, et ne promet pas ─────────────────────────────────
 * Il ne montre aucun délai, parce qu'aucun n'est garanti. Il dit en revanche
 * exactement ce qu'il en est, à l'endroit où l'on écrit : chaque message est
 * lu, il n'y a pas d'engagement de délai, et une demande relevant du RGPD suit
 * un autre chemin — celui-là a un délai légal.
 */

const CATEGORIES = [
  { valeur: 'fonctionnement', texte: 'Question sur le fonctionnement' },
  { valeur: 'anomalie', texte: 'Quelque chose ne marche pas' },
  { valeur: 'evolution', texte: 'Demande d’évolution' },
  { valeur: 'facturation', texte: 'Facturation ou abonnement' },
  { valeur: 'rgpd', texte: 'Données personnelles (accès, effacement)' },
  { valeur: 'retractation', texte: 'Rétractation (sous 14 jours)' },
  { valeur: 'autre', texte: 'Autre' },
] as const;

/**
 * La demande de rétractation, pré-remplie.
 *
 * C'est le lien « Ou faire la demande au support » du courriel de
 * confirmation (`/support?nouvelle=retractation`). Le texte reprend celui du
 * formulaire type : la demande est sans ambiguïté, et le ticket en garde la
 * date — c'est elle qui fait foi pour le délai.
 */
const DEMANDE_RETRACTATION = {
  sujet: 'Rétractation de mon abonnement',
  corps: 'Je vous notifie par la présente ma rétractation du contrat portant sur '
    + 'l’abonnement à Journal de Classe.',
};

/*
 * ── Ce qui reste ouvert sans abonnement en cours ────────────────────────────
 * Refuser d'entendre quelqu'un sur sa facture PARCE QUE sa facture ne passe pas
 * serait une boucle absurde ; et le droit d'accès à ses données personnelles ne
 * s'achète pas. Le serveur pose la même règle : ces deux-là passent toujours.
 */
const HORS_ABONNEMENT = new Set(['facturation', 'rgpd', 'retractation']);

/** Statuts d'abonnement qui ouvrent l'assistance. L'essai gratuit n'en est pas. */
const STATUTS_ABONNE = new Set(['TRIALING', 'ACTIVE', 'PAST_DUE']);

const LIBELLE_CATEGORIE = Object.fromEntries(
  CATEGORIES.map((c) => [c.valeur, c.texte]),
) as Record<string, string>;

const LIBELLE_STATUT: Record<string, string> = {
  OUVERT: 'En attente de réponse',
  REPONDU: 'Réponse reçue',
  CLOS: 'Clos',
  ANNULE: 'Annulé',
};

const TON_STATUT: Record<string, 'info' | 'succes' | 'attention'> = {
  OUVERT: 'attention',
  REPONDU: 'succes',
  // Clos et annulé partagent le ton neutre : ce sont deux fins, et aucune
  // n'appelle une action de l'enseignant.
  CLOS: 'info',
  ANNULE: 'info',
};

interface Ticket {
  id: string;
  category: string;
  subject: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

interface Fil extends Ticket {
  messages: Array<{ id: string; body: string; fromSupport: boolean; createdAt: string }>;
}

const dateCourte = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

// ═══════════════════════════════════════════════════════════════════════════
export function Support() {
  const { api } = useSession();
  /*
   * Le rôle avant la liste : un enseignant simple n'a pas de fil à voir, et la
   * requête lui reviendrait en 403. Mieux vaut une phrase qui explique qu'une
   * erreur qui accuse.
   */
  const moi = useDonnees<{ role: string }>('/auth/me');
  const responsable = !moi.donnees || moi.donnees.role !== 'MEMBER';
  const tickets = useDonnees<Ticket[]>(responsable ? '/support/tickets' : null, [responsable]);
  const { executer, enCours, erreur } = useEcriture();
  const { notifier } = useRetours();
  const naviguer = useNavigate();

  const abonnement = useAbonnement();
  /*
   * Tant que la réponse n'est pas revenue, on suppose l'abonnement en cours :
   * l'inverse ferait apparaître un avertissement d'un dixième de seconde à
   * chaque ouverture de l'écran, y compris chez les abonnés. Le serveur refuse
   * de toute façon ce qui n'est pas dû.
   */
  const abonne = !abonnement.donnees || STATUTS_ABONNE.has(abonnement.donnees.status);
  const categoriesOuvertes = abonne
    ? CATEGORIES
    : CATEGORIES.filter((c) => HORS_ABONNEMENT.has(c.valeur));

  const [ouvert, setOuvert] = useState(false);
  const [categorie, setCategorie] = useState<string>('fonctionnement');
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');

  // Arrivée depuis le courriel de confirmation : la demande s'ouvre, remplie.
  const [parametres, setParametres] = useSearchParams();
  useEffect(() => {
    if (parametres.get('nouvelle') !== 'retractation') return;
    setCategorie('retractation');
    setSujet(DEMANDE_RETRACTATION.sujet);
    setCorps(DEMANDE_RETRACTATION.corps);
    setOuvert(true);
    // Retiré de l'adresse : recharger la page ne doit pas rouvrir la fenêtre.
    setParametres({}, { replace: true });
  }, [parametres, setParametres]);

  async function envoyer(evenement: FormEvent) {
    evenement.preventDefault();
    const ok = await executer(() => api.requete('/support/tickets', {
      methode: 'POST',
      corps: { category: categorie, subject: sujet.trim(), body: corps.trim() },
    }));
    if (ok) {
      setOuvert(false);
      setSujet('');
      setCorps('');
      notifier('Votre demande est partie. Vous serez prévenu par courriel.');
      await tickets.recharger();
    }
  }

  if (!responsable) {
    return (
      <div className="flex flex-col gap-5">
        <EnTetePage titre="Support" contexte="Une école, un interlocuteur." />
        <Message ton="info" titre="C’est votre école qui écrit au support">
          <p>
            Les demandes sont adressées par la personne qui administre le compte
            de l’école. Signalez-lui ce qui vous bloque : elle transmettra si
            besoin, et vous aurez souvent la réponse plus vite qu’ici.
          </p>
          <p className="mt-2">
            Pour vos <strong>données personnelles</strong>, c’est également elle
            qu’il faut saisir : l’école est responsable de ces données, l’éditeur
            n’en est que le sous-traitant.
          </p>
          <p className="mt-2">
            La <strong>documentation</strong> vous est en revanche ouverte, et
            répond à la plupart des questions d’usage.
          </p>
        </Message>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <EnTetePage
        titre="Support"
        contexte="Poser une question, signaler un problème, proposer une évolution."
        actions={
          <Bouton
            variante="primary"
            icone={<IconePlus taille={16} />}
            onClick={() => {
              // Repartir d'une catégorie RECEVABLE : sans abonnement en cours,
              // « fonctionnement » n'est plus proposé, et laisser l'ancienne
              // valeur enverrait une demande que le serveur refuserait.
              setCategorie(categoriesOuvertes[0]!.valeur);
              setOuvert(true);
            }}
          >
            Nouvelle demande
          </Bouton>
        }
      />

      {erreur && <Message ton="erreur">{erreur}</Message>}

      <Carte titre="Vos demandes">
        {tickets.chargement && !tickets.donnees ? (
          <SqueletteTableau lignes={3} colonnes={3} />
        ) : (tickets.donnees ?? []).length === 0 ? (
          <Vide titre="Aucune demande">
            Une question sur le fonctionnement ? Commencez par la documentation,
            accessible en bas de la barre latérale, elle répond à l’essentiel.
          </Vide>
        ) : (
          <ul className="flex flex-col divide-y divide-[var(--color-border)]">
            {(tickets.donnees ?? []).map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => naviguer(`/support/${t.id}`)}
                  className="flex w-full flex-col gap-1 px-1 py-3 text-left hover:bg-[var(--color-surface-hover)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">{t.subject}</span>
                    <Etiquette ton={TON_STATUT[t.status] ?? 'info'}>
                      {LIBELLE_STATUT[t.status] ?? t.status}
                    </Etiquette>
                  </div>
                  <span className="text-[13px] text-[var(--color-fg-muted)]">
                    {LIBELLE_CATEGORIE[t.category] ?? t.category}
                    {` · ${dateCourte(t.createdAt)} · `}
                    {t.messageCount > 1 ? `${t.messageCount} messages` : '1 message'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Carte>

      <Dialogue titre="Nouvelle demande" ouvert={ouvert} surFermeture={() => setOuvert(false)}>
        <form onSubmit={envoyer} className="flex flex-col gap-4">
          <Selecteur
            libelle="De quoi s’agit-il ?"
            value={categorie}
            onChange={(e) => setCategorie(e.target.value)}
            options={categoriesOuvertes.map((c) => ({ valeur: c.valeur, texte: c.texte }))}
          />
          {categorie === 'retractation' && (
            <Message ton="info">
              Plus rapide : le bouton <strong>« Me rétracter »</strong> de l’écran
              Abonnement l’exerce tout de suite. Par cette demande, elle est traitée
              à la main ; la date d’envoi fait foi pour le délai de quatorze jours.
            </Message>
          )}
          <Champ
            libelle="Sujet"
            value={sujet}
            onChange={(e) => setSujet(e.target.value)}
            aide="En quelques mots, comme un titre de courriel."
            required
            autoFocus
          />
          <ZoneTexte
            libelle="Votre demande"
            value={corps}
            onChange={(e) => setCorps(e.target.value)}
            /*
             * ── Le rappel qui manque partout ailleurs ────────────────────────
             * Un ticket est du texte libre, et le premier réflexe est d'y
             * écrire « impossible de supprimer Dupont Marie ». Un nom d'élève
             * dans un fil de support, c'est une donnée de mineur qui sort de
             * l'application, transite par un courriel et se conserve avec le
             * ticket. L'identifiant de la classe suffit toujours.
             */
            aide="Décrivez ce que vous faisiez et ce qui s’est passé. N’écrivez pas de nom d’élève : décrivez la situation."
            rows={7}
            required
          />
          <ActionsDialogue>
            <Bouton variante="ghost" onClick={() => setOuvert(false)}>Annuler</Bouton>
            <Bouton
              type="submit"
              variante="primary"
              chargement={enCours}
              disabled={sujet.trim().length < 3 || corps.trim().length < 10}
            >
              Envoyer
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export function FilSupport() {
  const { id } = useParams();
  const { api } = useSession();
  const fil = useDonnees<Fil>(id ? `/support/tickets/${id}` : null, [id]);
  const { executer, enCours, erreur } = useEcriture();
  const { confirmer } = useRetours();
  const naviguer = useNavigate();
  const [reponse, setReponse] = useState('');

  const t = fil.donnees;

  async function repondre(evenement: FormEvent) {
    evenement.preventDefault();
    if (!id) return;
    const ok = await executer(() => api.requete(`/support/tickets/${id}/messages`, {
      methode: 'POST', corps: { body: reponse.trim() },
    }));
    if (ok) { setReponse(''); await fil.recharger(); }
  }

  async function annuler() {
    if (!id) return;
    const oui = await confirmer({
      titre: 'Annuler cette demande ?',
      message: 'Le support cessera de la traiter. Vous pourrez en ouvrir une autre.',
      libelleConfirmation: 'Annuler la demande',
    });
    if (!oui) return;
    const ok = await executer(() => api.requete(`/support/tickets/${id}/cancel`, {
      methode: 'POST',
    }));
    if (ok) await fil.recharger();
  }

  if (fil.chargement && !t) return <SqueletteTableau lignes={4} colonnes={1} />;
  if (!t) return <Message ton="erreur">Cette demande est introuvable.</Message>;

  return (
    <div className="flex flex-col gap-5">
      <EnTetePage
        titre={t.subject}
        contexte={`${LIBELLE_CATEGORIE[t.category] ?? t.category} · ouverte le ${dateCourte(t.createdAt)}`}
        actions={
          <>
            <Bouton variante="ghost" onClick={() => naviguer('/support')}>
              Retour
            </Bouton>
            {t.status !== 'ANNULE' && t.status !== 'CLOS' && (
              <Bouton variante="secondary" onClick={() => void annuler()}>
                Annuler la demande
              </Bouton>
            )}
          </>
        }
      />

      {erreur && <Message ton="erreur">{erreur}</Message>}

      <Carte>
        <div className="flex flex-col gap-4">
          {t.messages.map((m) => (
            <div
              key={m.id}
              className={[
                'rounded-lg border p-3',
                m.fromSupport
                  // La réponse du support se distingue au premier coup d'œil :
                  // dans un fil de six messages, on cherche la dernière.
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                  : 'border-[var(--color-border)] bg-[var(--color-surface-raised)]',
              ].join(' ')}
            >
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
                {m.fromSupport ? 'Support' : 'Vous'}
                {` · ${dateCourte(m.createdAt)}`}
              </div>
              <p className="whitespace-pre-wrap text-[14px]">{m.body}</p>
            </div>
          ))}
        </div>
      </Carte>

      {/*
        * Une demande CLOSE est fermée, comme une demande annulée : on n'y
        * répond plus. Une suite passe par une nouvelle demande — le support,
        * lui, peut rouvrir le fil s'il le juge utile.
        */}
      {t.status === 'ANNULE' || t.status === 'CLOS' ? (
        <Message ton="info">
          {t.status === 'ANNULE'
            ? 'Cette demande est annulée. Ouvrez-en une nouvelle si besoin.'
            : 'Cette demande est close. Ouvrez-en une nouvelle si besoin.'}
        </Message>
      ) : (
        <Carte titre="Répondre">
          <form onSubmit={repondre} className="flex flex-col gap-3">
            <ZoneTexte
              libelle="Votre message"
              value={reponse}
              onChange={(e) => setReponse(e.target.value)}
              rows={5}
            />
            <div className="flex justify-end">
              <Bouton
                type="submit"
                variante="primary"
                chargement={enCours}
                disabled={reponse.trim().length === 0}
              >
                Envoyer
              </Bouton>
            </div>
          </form>
        </Carte>
      )}
    </div>
  );
}
